import React, { useState, useEffect, useRef } from "react";
import LoadingImage from "./LoadingImage";
import { getTransformedCloudinaryUrl } from "../utils/cloudinary";

const ProductModal = ({
  selectedProduct,
  currentImageIndex,
  imagesArray,
  handlePrevImage,
  handleNextImage,
  setCurrentImageIndex,
  handleAddToCart,
  cartItems,
  setShowProductModal,
  setShowCheckoutModal,
  showProductModal,
  handleCloseModal,
}) => {
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const cartItem = cartItems?.find((item) => item.id === selectedProduct?.id);
  const initialQuantity = cartItem ? cartItem.quantity : 1;
  const [selectedQuantity, setSelectedQuantity] = useState(initialQuantity);

  useEffect(() => {
    if (cartItem && cartItem.quantity !== selectedQuantity) {
      setSelectedQuantity(cartItem.quantity);
    }
    if (!cartItem && selectedQuantity !== 1) {
      setSelectedQuantity(1);
    }
    // eslint-disable-next-line
  }, [cartItem?.quantity, cartItem, selectedProduct?.id]);

  if (!selectedProduct) return null;

  const isInCart = cartItems?.some((item) => item.id === selectedProduct.id);

  // --- Swipe logic ---
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const distance = touchStartX.current - touchEndX.current;
    if (distance > 50) {
      handleNextImage();
    } else if (distance < -50) {
      handlePrevImage();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Helper: is this a product (not a service)?
  const isProduct = !!selectedProduct.size;

  return (
    <div
      className={`mx-auto flex flex-col bg-white rounded-2xl shadow-xl ${
        isProduct ? "max-w-lg w-full h-full p-4" : "max-w-sm p-4"
      }`}
      style={{
        maxWidth: isProduct ? "90vw" : "400px",
        margin: "0 auto",
        boxSizing: "border-box",
        overflow: "auto",
        touchAction: "manipulation",
      }}
    >
      {/* Modal Header with Centered Title */}
      <div className="w-full text-center pb-4 border-b border-gray-200">
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">
          {selectedProduct.title || selectedProduct.name}
        </h2>
      </div>

      {/* Main content below the title */}
      {isProduct ? (
        // Product: two-column layout
        <div className="flex flex-col lg:flex-row items-stretch pt-4 w-full h-full overflow-y-auto relative">
          {/* Left: Images */}
          <div className="w-full lg:w-1/2 flex flex-col items-center justify-center mb-6 lg:mb-0">
            {/* Only show images if this is a product (has a size property) */}
            {isProduct ? (
              <div
                className="relative w-full mb-4 flex-grow flex items-center justify-center"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {/* Previous Button */}
                <button
                  onClick={handlePrevImage}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 rounded-full p-2 shadow hover:bg-indigo-100 transition-colors z-10"
                  aria-label="Previous image"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="28"
                    height="28"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide-icon"
                  >
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>
                {/* Preload all thumbnails invisibly */}
                {imagesArray.map((img, idx) => (
                  <img
                    key={idx}
                    src={getTransformedCloudinaryUrl(
                      img,
                      "w_600,q_auto,f_auto"
                    )}
                    alt={`Thumbnail ${idx + 1}`}
                    style={{ display: "none" }}
                  />
                ))}

                {/* Main Image */}
                <img
                  src={getTransformedCloudinaryUrl(
                    imagesArray[currentImageIndex],
                    "w_1000,q_auto,f_auto"
                  )}
                  alt={selectedProduct.title || selectedProduct.name}
                  className="rounded-3xl shadow-lg w-full max-h-[40vh] sm:max-h-[60vh] object-contain"
                  style={{ minHeight: "200px" }}
                />
                {/* Next Button */}
                <button
                  onClick={handleNextImage}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 rounded-full p-2 shadow hover:bg-indigo-100 transition-colors z-10"
                  aria-label="Next image"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="28"
                    height="28"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide-icon"
                  >
                    <polyline points="9 6 15 12 9 18"></polyline>
                  </svg>
                </button>
              </div>
            ) : (
              // If it's a service (no size), show nothing here
              <div className="w-full mb-4 flex-grow flex items-center justify-center" />
            )}

            {/* Thumbnail gallery for mobile view */}
            {isProduct && (
              <div className="flex flex-wrap justify-center gap-2 lg:hidden">
                {imagesArray.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`rounded-xl overflow-hidden transform transition-transform duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      currentImageIndex === index
                        ? "border-2 border-indigo-600"
                        : "border-2 border-transparent"
                    }`}
                  >
                    <LoadingImage
                      src={getTransformedCloudinaryUrl(
                        img,
                        "w_200,q_auto,f_auto"
                      )}
                      alt={`Thumbnail ${index + 1} of ${
                        selectedProduct.title || selectedProduct.name
                      }`}
                      className="w-16 h-16 object-cover"
                      onClick={() => setCurrentImageIndex(index)}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Right: Info */}
          <div className="w-full lg:w-1/2 lg:pl-10 mt-6 lg:mt-0 flex flex-col h-full justify-between text-center lg:text-left">
            <div>
              <p
                className={`text-lg text-gray-700 mb-4 ${
                  !isProduct ? "text-center" : ""
                }`}
              >
                {selectedProduct.description}
              </p>
              <div
                className={`mb-6 ${
                  !isProduct
                    ? "flex justify-center"
                    : "flex items-center justify-between"
                }`}
              >
                <div className="flex items-center"></div>
                <span
                  className={`text-3xl sm:text-4xl font-bold text-indigo-600 ${
                    !isProduct ? "text-center" : ""
                  }`}
                >
                  ${(selectedProduct.price / 100).toFixed(2)}
                </span>
              </div>
              {/* Thumbnail gallery for web view */}
              {isProduct && (
                <div className="hidden lg:flex flex-wrap justify-center lg:justify-start gap-2 mb-6">
                  {imagesArray.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`rounded-xl overflow-hidden transform transition-transform duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        currentImageIndex === index
                          ? "border-2 border-indigo-600"
                          : "border-2 border-transparent"
                      }`}
                    >
                      <LoadingImage
                        src={getTransformedCloudinaryUrl(
                          img,
                          "w_200,q_auto,f_auto"
                        )}
                        alt={`Thumbnail ${index + 1} of ${
                          selectedProduct.title || selectedProduct.name
                        }`}
                        className="w-16 h-16 object-cover"
                        onClick={() => setCurrentImageIndex(index)}
                      />
                    </button>
                  ))}
                </div>
              )}
              <div
                className="hidden lg:block"
                style={{ height: "1.5rem" }}
              ></div>
            </div>
            <div>
              {/* Quantity input and Add to Cart button */}
              <div
                className={`mb-4 ${
                  !isProduct
                    ? "flex flex-col items-center"
                    : "flex items-center justify-center lg:justify-start"
                }`}
              >
                {selectedProduct.stock > 1 && (
                  <div className="flex flex-col items-center space-y-1 sm:items-center sm:space-y-0 ml-4 sm:ml-0">
                    {/* ↑↑↑ Add ml-4 for mobile, sm:ml-0 for desktop/tablet */}
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        className="px-3 py-1 rounded-full bg-gray-200 text-xl font-bold hover:bg-gray-300 transition"
                        onClick={() =>
                          setSelectedQuantity((q) => Math.max(1, q - 1))
                        }
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <span className="w-10 text-center text-lg font-semibold">
                        {selectedQuantity}
                      </span>
                      <button
                        type="button"
                        className="px-3 py-1 rounded-full bg-gray-200 text-xl font-bold hover:bg-gray-300 transition"
                        onClick={() =>
                          setSelectedQuantity((q) =>
                            Math.min(Number(selectedProduct.stock), q + 1)
                          )
                        }
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                    <span className="text-sm text-gray-500 font-bold ml-4 sm:ml-0">
                      {selectedProduct.stock} available
                    </span>
                  </div>
                )}
                {isInCart ? (
                  <div
                    className={`${
                      !isProduct
                        ? "w-full flex justify-center"
                        : "flex justify-end w-full"
                    }`}
                  >
                    <button
                      onClick={() => {
                        handleAddToCart(selectedProduct, selectedQuantity);
                        setShowProductModal(false);
                        setShowCheckoutModal(true);
                      }}
                      className="min-w-[160px] max-w-[220px] py-3 px-6 mt-8 sm:min-w-[120px] sm:max-w-[180px] sm:py-2 sm:px-4 sm:mt-4 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition-colors font-bold text-lg sm:text-base lg:py-3 lg:px-10 lg:text-xl"
                      // ↑↑↑ Bigger on mobile: wider, taller, larger text; normal on tablet/desktop
                    >
                      Return to Cart
                    </button>
                  </div>
                ) : (
                  <div
                    className={`${
                      !isProduct
                        ? "w-full flex justify-center"
                        : "flex justify-end w-full"
                    }`}
                  >
                    <button
                      onClick={() =>
                        handleAddToCart(selectedProduct, selectedQuantity)
                      }
                      className="min-w-[160px] max-w-[220px] py-3 px-6 mt-8 sm:min-w-[120px] sm:max-w-[180px] sm:py-2 sm:px-4 sm:mt-4 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition-colors font-bold text-lg sm:text-base lg:py-3 lg:px-10 lg:text-xl"
                      // ↑↑↑ Bigger on mobile: wider, taller, larger text; normal on tablet/desktop
                    >
                      Add to Cart
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Service: single-column, centered layout, no h-full, no w-full, less padding
        <div className="flex flex-col items-center justify-center pt-4 pb-2">
          <p className="text-xl sm:text-2xl text-gray-700 mb-4 text-center leading-relaxed max-w-xs sm:max-w-sm">
            {selectedProduct.description}
          </p>
          <span className="text-4xl sm:text-5xl font-bold text-indigo-600 mb-4 text-center block">
            ${(selectedProduct.price / 100).toFixed(2)}
          </span>
          {selectedProduct.stock > 1 && (
            <div className="flex flex-col items-center space-y-2 mb-2">
              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  className="px-4 py-2 rounded-full bg-gray-200 text-2xl font-bold hover:bg-gray-300 transition"
                  onClick={() => setSelectedQuantity((q) => Math.max(1, q - 1))}
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <span className="w-12 text-center text-2xl font-semibold">
                  {selectedQuantity}
                </span>
                <button
                  type="button"
                  className="px-4 py-2 rounded-full bg-gray-200 text-2xl font-bold hover:bg-gray-300 transition"
                  onClick={() =>
                    setSelectedQuantity((q) =>
                      Math.min(Number(selectedProduct.stock), q + 1)
                    )
                  }
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
              <span className="text-base text-gray-500 font-bold">
                {selectedProduct.stock} available
              </span>
            </div>
          )}
          <button
            onClick={() => {
              handleAddToCart(selectedProduct, selectedQuantity);
              if (isInCart) {
                setShowProductModal(false);
                setShowCheckoutModal(true);
              }
            }}
            className="w-full py-3 px-6 mt-2 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition-colors font-bold text-lg text-center"
          >
            {isInCart ? "Return to Cart" : "Add to Cart"}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductModal;
